import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'
import { useLayoutMode } from '~/composables/use-layout-mode'

type ContractActivationEventData = {
    contract_reference_number: number
    plan_type: string
    plan_name: string
    facility_name: string
    kiosk: boolean
    invoice_generated: boolean
}

function emitContractActivationGaAndDataLayer(input: {
    shouldTrackGtag: boolean
    shouldTrackDataLayer: boolean
    eventData: ContractActivationEventData
}) {
    if (input.shouldTrackGtag) {
        try {
            // This event intentionally has no value/items payload because no invoice exists.
            // @ts-ignore : ignore gtag not defined
            gtag('event', 'contract_activation', input.eventData)
        } catch {
            // no-op
        }
    }

    if (input.shouldTrackDataLayer) {
        try {
            // This mirrors the GA event for GTM users without using ecommerce purchase semantics.
            // @ts-ignore : Ignore dataLayer object not defined
            window.dataLayer.push({
                event: 'contract_activation',
                ...input.eventData
            })
        } catch {
            // no-op
        }
    }
}

export const ContractActivationAnalyticsFragment = graphql(`
    fragment ContractActivationAnalyticsFragment on PlanContract {
        referenceNumber
        planType
        publicName
        facility {
            shortName
            organization {
                googleAnalyticsTag
                googleAnalyticsTagEcommerceEvents
                googleAnalyticsTagKioskEcommerceEvents
                dataLayerEcommerceEvents
                dataLayerKioskEcommerceEvents
            }
        }
    }
`)

export function useContractActivationAnalytics() {
    const { isKiosk } = useLayoutMode()

    function emitContractActivationEvent(
        contract: FragmentType<typeof ContractActivationAnalyticsFragment>
    ) {
        const contractData = getFragmentData(
            ContractActivationAnalyticsFragment,
            contract
        )

        const shouldTrackGtag =
            contractData.facility.organization.googleAnalyticsTag !== '' &&
            (!isKiosk.value ||
                contractData.facility.organization
                    .googleAnalyticsTagKioskEcommerceEvents) &&
            contractData.facility.organization.googleAnalyticsTagEcommerceEvents
        const shouldTrackDataLayer =
            (!isKiosk.value ||
                contractData.facility.organization
                    .dataLayerKioskEcommerceEvents) &&
            contractData.facility.organization.dataLayerEcommerceEvents

        if (!shouldTrackGtag && !shouldTrackDataLayer) {
            return
        }

        emitContractActivationGaAndDataLayer({
            shouldTrackGtag,
            shouldTrackDataLayer,
            eventData: {
                contract_reference_number: contractData.referenceNumber,
                plan_type: contractData.planType,
                plan_name: contractData.publicName,
                facility_name: contractData.facility.shortName,
                kiosk: isKiosk.value,
                invoice_generated: false
            }
        })
    }

    return {
        emitContractActivationEvent
    }
}
